import { type ApolloQueryResult } from "@apollo/client";

import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { AsyncSelect, FormModal, Transfer } from "@neonumy/neonumy-ui";
import { Col, Form, Row } from "antd5";
import { LIST_ACCOUNTING_FIRMS_SELECT_QUERY } from "queries/centralapi/accountingFirms";
import { useTranslation } from "react-i18next";
import { useCreateUpdateUserAccess } from "./hooks/useCreateUpdateUserAccess";

interface CreateUpdateUserAccessPropsType {
  open: boolean;
  closeModal: () => void;
  userId: ID;
  /** No id is add: only then is the accounting firm editable. */
  accountingFirmId?: ID;
  /** The firms the user already has, kept out of the select. */
  accountingFirmsId?: ID[];
  refetch: (
    variables?: Partial<ListAccountingFirmsAccessInput> | undefined,
  ) => Promise<ApolloQueryResult<ListAccountingFirmsAccessOutput>>;
}

export default function CreateUpdateUserAccess({
  open,
  closeModal,
  userId,
  accountingFirmId,
  accountingFirmsId,
  refetch,
}: CreateUpdateUserAccessPropsType) {
  const { t } = useTranslation();

  const { form, companiesOptions, isSaving, onFinish } = useCreateUpdateUserAccess({
    open,
    closeModal,
    userId,
    accountingFirmId,
    refetch,
  });

  return (
    <FormModal
      open={open}
      closeModal={closeModal}
      title={accountingFirmId ? t("Edit user access") : t("Add Access to user")}
      form={form}
      onFinish={onFinish}
      isSaving={isSaving}
      width={800}
    >
      <Row>
        <Col span={24}>
          <Form.Item
            label={t("Accounting firm")}
            name="accountingFirm"
            rules={[{ required: true }]}
          >
            <AsyncSelect
              queryOptions={{
                client: clientCentralApiStrawberry,
                query: LIST_ACCOUNTING_FIRMS_SELECT_QUERY,
                dataName: "listAccountingFirmsPaginated",
                getVariables: (search, prevOptions) => ({
                  limit: 20,
                  orderBy: ["name"],
                  offset: prevOptions.length,
                  filter: {
                    exclude_Id_In: accountingFirmsId,
                    deletedAt_Isnull: true,
                    search,
                  },
                }),
              }}
              optionsFormat={item => ({
                label: item.name,
                value: item.id,
              })}
              isDisabled={!!accountingFirmId}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label={t("Companies")} name="companies" valuePropName="targetKeys">
            <Transfer
              dataSource={companiesOptions}
              titles={[t("Available"), t("Selected")]}
              render={(item: CompanyOptionType) => `${item.internalReference} - ${item.title}`}
            />
          </Form.Item>
        </Col>
      </Row>
    </FormModal>
  );
}
